import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export interface AssetNavigationContext {
  mode: 'view' | 'edit' | 'create';
  id?: number;
  itemName?: string;
  parentId?: number | null;
}

export interface AssetNavigationResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
  fallbackLabel: string;
}

/**
 * Builds navigation for asset pages with hierarchical parent/child support.
 *
 * Unlike standard flat entities, assets have a tree structure where:
 * - Child assets show "Parent Asset" breadcrumb linking to parent's children tab
 * - Back button navigates to parent asset (if child) or assets list (if root)
 */
function buildAssetNavigation(
  context: AssetNavigationContext
): AssetNavigationResult {
  const {mode, id, itemName, parentId} = context;

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const breadcrumbs: BreadcrumbItem[] = [
    {label: 'Assets', to: PathUtility.Assets()},
  ];

  if (parentId) {
    breadcrumbs.push({
      label: 'Parent Asset',
      to: PathUtility.AssetView(parentId, 'children'),
    });
  }

  if (isCreateMode) {
    breadcrumbs.push({label: 'New Asset'});
  } else if (isEditMode && id) {
    breadcrumbs.push({
      label: itemName ?? 'Asset',
      to: PathUtility.AssetView(id, 'overview'),
    });
    breadcrumbs.push({label: 'Edit'});
  } else {
    breadcrumbs.push({label: itemName ?? 'Asset'});
  }

  let fallbackTo: string;
  let fallbackLabel: string;

  if (isEditMode && id) {
    fallbackTo = PathUtility.AssetView(id, 'overview');
    fallbackLabel = 'Cancel';
  } else if (parentId) {
    fallbackTo = PathUtility.AssetView(parentId, 'children');
    fallbackLabel = 'Back to Parent';
  } else {
    fallbackTo = PathUtility.Assets();
    fallbackLabel = 'Back to Assets';
  }

  return {breadcrumbs, fallbackTo, fallbackLabel};
}

/**
 * Hook for asset page navigation with hierarchical parent/child support.
 *
 * @example
 * ```tsx
 * const navigation = useAssetNavigation({
 *   mode: 'view',
 *   id: 123,
 *   itemName: 'Forklift #1',
 *   parentId: item?.parentId,
 * });
 *
 * <BackBar
 *   fallbackTo={navigation.fallbackTo}
 *   fallbackLabel={navigation.fallbackLabel}
 *   breadcrumbs={navigation.breadcrumbs}
 * />
 * ```
 */
export function useAssetNavigation(
  context: AssetNavigationContext
): AssetNavigationResult {
  return useMemo(
    () => buildAssetNavigation(context),
    [context.mode, context.id, context.itemName, context.parentId]
  );
}
